import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Info, Search } from 'lucide-react';
import type { PanelOrganizationRowDto } from '@icebreaker/shared';
import { Avatar, OrgMark, Button, EmptyState, Input, Skeleton, Table, Tag } from '@icebreaker/ui';
import { STAFF_PERMISSIONS } from '@icebreaker/shared/staff';
import { organizationDetailPath, salesInquiryDetailPath } from '@/app/routes';
import { selectHasStaffPermission } from '@/features/auth/store/auth.selectors';
import adminStyles from '@/features/admin/ui/admin.module.css';
import { isLinkableLead } from '@/features/sales/model/sales-query';
import { numericDateOptions } from '@/features/sales/model/sales-view';
import { fetchSalesInquiryPicker } from '@/features/sales/store/sales-inquiries.slice';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { ORGANIZATION_PAGE_SIZE, toOrganizationQuery } from '../model/organization-query';
import { ownerView } from '../model/owner-view';
import { fetchOrganizations, organizationFilterChanged } from '../store/organizations.slice';
import { CreateOrganizationDialog } from './create-organization-dialog';
import styles from './organizations.module.css';

/**
 * KURUMLAR LİSTESİ (P3). Kurumlar yalnız panelden açılır: tek dolu birincil
 * eylem başlıktaki "Kurum aç"tır (P4). Boş ve arama-boş hâllerde ikinci bir
 * dolu düğme ÇİZİLMEZ (ekran başına tek dolu eylem, README 4.7). Liste hata verse de "Kurum aç" açık kalır, listeden
 * bağımsızdır (referans HATA hâli).
 */
export function OrganizationsScreen() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const { filters, list } = useAppSelector((root: RootState) => root.panelOrgs);
  const leadHints = useAppSelector((root: RootState) => root.panelSales.picker);
  const canReadLeads = useAppSelector(
    selectHasStaffPermission(STAFF_PERMISSIONS.SALES_INQUIRY_MANAGE),
  );
  const [searchDraft, setSearchDraft] = useState(filters.q);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(() => {
    dispatch(fetchOrganizations.request(toOrganizationQuery(filters)));
  }, [dispatch, filters]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (searchDraft === filters.q) return undefined;
    const handle = window.setTimeout(
      () => dispatch(organizationFilterChanged({ q: searchDraft })),
      300,
    );
    return () => window.clearTimeout(handle);
  }, [dispatch, filters.q, searchDraft]);

  useEffect(() => {
    const onOnline = () => refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  const query = filters.q.trim();
  const searchEmpty = list.phase === 'succeeded' && list.items.length === 0 && query !== '';

  // Arama sonucu boşsa satış adaylarında aynı ad aranır (referans P3 "arama sonucu boş").
  useEffect(() => {
    if (searchEmpty && canReadLeads) {
      dispatch(fetchSalesInquiryPicker.request({ q: query, page: 1, pageSize: 5 }));
    }
  }, [canReadLeads, dispatch, query, searchEmpty]);

  const firstLoad = list.phase === 'loading' && list.items.length === 0;
  const from = list.total === 0 ? 0 : (list.page - 1) * ORGANIZATION_PAGE_SIZE + 1;
  const to = Math.min(list.page * ORGANIZATION_PAGE_SIZE, list.total);
  const leadMatch =
    searchEmpty && leadHints.query === query
      ? (leadHints.items.find((row) => isLinkableLead(row) && row.companyName) ?? null)
      : null;

  return (
    <section className={adminStyles.screen}>
      <div className={styles.titleRow}>
        <h1 className={adminStyles.title}>{t.t('admin.orgs.title')}</h1>
        {list.phase === 'succeeded' && query === '' ? (
          <span className={adminStyles.titleMeta}>
            {t.t('admin.orgs.total', { count: list.total })}
          </span>
        ) : null}
        <Button variant="primary" onClick={() => setCreating(true)}>
          {t.t('admin.orgs.create')}
        </Button>
      </div>
      <div className={styles.toolbar}>
        <label className={styles.search}>
          <Search size={16} aria-hidden="true" className={styles.searchIcon} />
          <Input
            className={styles.searchInput}
            type="search"
            aria-label={t.t('admin.orgs.searchLabel')}
            placeholder={t.t('admin.orgs.searchPlaceholder')}
            value={searchDraft}
            disabled={firstLoad}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <span className={styles.toolbarNote}>
          <Info size={14} aria-hidden="true" /> {t.t('admin.orgs.auditNote')}
        </span>
      </div>

      {list.phase === 'failed' && list.items.length === 0 ? (
        <div className={styles.errorCard} role="alert">
          <p className={styles.errorTitle}>{t.t('admin.orgs.errorTitle')}</p>
          <p className={styles.errorBody}>{t.t('admin.orgs.errorBody')}</p>
          <Button variant="secondary" size="sm" onClick={refresh}>
            {t.t('admin.orgs.retry')}
          </Button>
        </div>
      ) : list.phase === 'succeeded' && list.items.length === 0 ? (
        <div className={styles.stateBox}>
          {query !== '' ? (
            <EmptyState
              title={t.t('admin.orgs.searchEmptyTitle', { q: query })}
              body={
                leadMatch ? (
                  <span className={styles.leadHint}>
                    {t.t('admin.orgs.searchEmptyLead', { name: leadMatch.companyName ?? '' })}
                    <Link to={salesInquiryDetailPath(leadMatch.id)}>
                      {t.t('admin.orgs.searchEmptyLeadAction')}
                    </Link>
                  </span>
                ) : undefined
              }
            />
          ) : (
            <EmptyState
              title={t.t('admin.orgs.emptyTitle')}
              body={t.t('admin.orgs.emptyBody')}
              preview={<ListPreview />}
            />
          )}
        </div>
      ) : (
        <div className={[styles.tableArea, 'ib-priority-table'].join(' ')}>
          <Table aria-label={t.t('admin.orgs.title')}>
            <thead>
              <tr>
                <th>{t.t('admin.orgs.table.organization')}</th>
                <th>{t.t('admin.orgs.table.verification')}</th>
                <th>{t.t('admin.orgs.table.owner')}</th>
                <th className={styles.numberHead}>{t.t('admin.orgs.table.credits')}</th>
                <th>
                  <span className={styles.headWithNote}>
                    {t.t('admin.orgs.table.contractEnd')}
                    <span className={[styles.hiddenNote, 'ib-col-note'].join(' ')}>
                      {t.t('admin.sales.hiddenColumns', { count: 2 })}
                    </span>
                  </span>
                </th>
                <th className={[styles.optionalCol, 'ib-col-optional'].join(' ')}>
                  {t.t('admin.orgs.table.source')}
                </th>
                <th className={[styles.optionalCol, 'ib-col-optional'].join(' ')}>
                  {t.t('admin.orgs.table.createdAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {firstLoad
                ? Array.from({ length: 4 }, (_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td colSpan={7}>
                        {index === 0 ? (
                          <Skeleton label={t.t('admin.orgs.loading')} lines={1} lineHeight={14} />
                        ) : (
                          <Skeleton decorative lines={1} lineHeight={14} />
                        )}
                      </td>
                    </tr>
                  ))
                : list.items.map((row) => <OrganizationRow key={row.id} row={row} />)}
            </tbody>
          </Table>
        </div>
      )}

      {list.total > ORGANIZATION_PAGE_SIZE ? (
        <div className={styles.pager}>
          <span className={styles.pagerRange}>
            {t.t('admin.sales.range', {
              from: t.formatNumber(from),
              to: t.formatNumber(to),
              total: t.formatNumber(list.total),
            })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={filters.page <= 1}
            onClick={() =>
              dispatch(organizationFilterChanged({ page: Math.max(1, filters.page - 1) }))
            }
          >
            {t.t('admin.overview.prevPage')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!list.hasNext}
            onClick={() => dispatch(organizationFilterChanged({ page: filters.page + 1 }))}
          >
            {t.t('admin.overview.nextPage')}
          </Button>
        </div>
      ) : null}

      {creating ? <CreateOrganizationDialog onClose={() => setCreating(false)} /> : null}
    </section>
  );
}

function OrganizationRow({ row }: { row: PanelOrganizationRowDto }) {
  const t = useTranslation();
  const owner = ownerView(row.owner);
  const exhausted = !row.credits.contractActive && row.credits.available === 0;
  return (
    <tr className={styles.clickableRow}>
      <td className={styles.orgCol}>
        <span className={styles.orgCell}>
          <OrgMark name={row.name} logoUrl={row.logoUrl} fill={row.brandFill} size="sm" />
          <Link
            className={[styles.rowLink, styles.orgName].join(' ')}
            to={organizationDetailPath(row.id)}
          >
            {row.name}
          </Link>
        </span>
      </td>
      <td>
        <VerificationTag status={row.verificationStatus} />
      </td>
      <td>
        <span className={styles.personCell}>
          {owner.kind === 'active' ? (
            <>
              <Avatar name={owner.name ?? t.t('admin.orgs.owner.removedAccount')} size="xs" />
              <span className={styles.personName}>
                {owner.name ?? t.t('admin.orgs.owner.removedAccount')}
              </span>
            </>
          ) : (
            <span className={[styles.personName, styles.ownerPending].join(' ')}>
              {t.t(`admin.orgs.owner.${owner.kind}`)}
            </span>
          )}
        </span>
      </td>
      <td className={[styles.numberCell, exhausted ? styles.warnNumber : ''].join(' ')}>
        {row.credits.contractActive ? '—' : t.formatNumber(row.credits.available)}
      </td>
      <td className={styles.dateCell}>
        {row.credits.contractEndsAt
          ? t.formatDate(row.credits.contractEndsAt, numericDateOptions(t.locale))
          : '—'}
      </td>
      <td className={[styles.optionalCol, 'ib-col-optional'].join(' ')}>
        <Tag variant="interest">{t.t(`admin.orgs.sources.${row.provisioningSource}`)}</Tag>
      </td>
      <td className={[styles.createdCell, styles.optionalCol, 'ib-col-optional'].join(' ')}>
        {t.formatDate(row.createdAt, numericDateOptions(t.locale))}
      </td>
    </tr>
  );
}

export function VerificationTag({
  status,
}: {
  status: PanelOrganizationRowDto['verificationStatus'];
}) {
  const t = useTranslation();
  if (status === 'verified') {
    return (
      <Tag variant="success" className={styles.verifiedTag}>
        <Check size={12} aria-hidden="true" />
        {t.t('admin.orgs.verification.verified')}
      </Tag>
    );
  }
  return <Tag variant="neutral">{t.t(`admin.orgs.verification.${status}`)}</Tag>;
}

function ListPreview() {
  return (
    <>
      {['72%', '54%', '64%'].map((width) => (
        <div key={width} className={styles.previewRow}>
          <span className={styles.previewMark} />
          <span className={styles.previewBar} style={{ width }} />
          <span className={styles.previewBar} />
          <span className={styles.previewBar} style={{ width }} />
          <span className={styles.previewBar} />
        </div>
      ))}
    </>
  );
}
